<script setup lang="ts">
import { computed, toRefs } from "vue";
import { AccordionStatus, AccordionId } from "@/stores/mainView";
import { useGrowthBookFeatures } from "@/composables/growthbook";

const props = defineProps<{
    id: AccordionId,
    label: string,
    subheading?: string,
    ctaText: string,
    labelIndex: number,
    open: boolean,
    locked: boolean,
    status: AccordionStatus,
    keepRendered?: boolean
}>();
const emit = defineEmits<{
    (on: "toggleAccordion", target: AccordionId): void,
    (on: "ctaClicked", id: AccordionId): void
}>();

const { open } = toRefs(props);

function handleCtaClicked() {
    emit("ctaClicked", props.id);
}

const accordionHeaderStateClass = computed(() => {
    switch (props.status) {
        case AccordionStatus.Success:
            return "header-success";
        case AccordionStatus.Error:
            return "alert-danger";
        case AccordionStatus.Incomplete:
            return "";
        default:
            return "";
    }
});

const accordionTextStateClass = computed(() => {
    switch (props.status) {
        case AccordionStatus.Success:
            return "text-success";
        case AccordionStatus.Error:
            return "text-danger";
        case AccordionStatus.Incomplete:
            return "";
        default:
            return "";
    }
});

const toggleAccordion = () => {
    if (props.locked) {
        return;
    }
    emit("toggleAccordion", props.id);
};

const { isStickyCtaEnabled } = useGrowthBookFeatures();

</script>
<template>
    <div
        :id="`accordion_${id}`"
        class="accordion"
    >
        <div class="card">
            <div
                :id="`heading_${id}`"
                :aria-controls="id"
                aria-expanded="true"
                :class="`card-header  ${props.locked ? '' : ' show-pointer'} ${accordionHeaderStateClass}`"
                @click="toggleAccordion"
            >
                <h5
                    class="mb-0 disable-selection questiontitle"
                    :class="accordionTextStateClass"
                >
                    <span>
                        <div class="circle-number mb-2 mb-md-0">{{ labelIndex }}</div>
                        <div :class="subheading ? 'pt-header-small' : 'pt-header'">{{ label }}
                            <span
                                v-if="subheading"
                                class="subheading"
                            >{{ subheading }}</span>
                            <img
                                class="down-arrow d-none"
                                src="../assets/images/down-arrow.svg"
                                alt=""
                            ></div>
                    </span>
                </h5>
            </div>
            <Transition mode="out-in">
                <div
                    v-if="keepRendered === true || open"
                    v-show="open"
                    :id="id"
                    :aria-labelledby="`heading_${id}`"
                    data-test-id="accordion-content"
                >
                    <div class="card-body-new">
                        <div>
                            <slot ref="childComponent" />
                        </div>
                        <div
                            :class="`row ${id === AccordionId.ImportantInformation && isStickyCtaEnabled ? 'btn-sticky' : ''}`"
                        >
                            <div class="col-12 mt-3 mx-auto" />
                            <button
                                :id="id"
                                :data-test="`${id}-cta-button`"
                                type="button"
                                :name="id"
                                class="btn btn-forward btn-block"
                                @click="handleCtaClicked"
                            >
                                {{ ctaText }}
                            </button>
                        </div>
                    </div>
                </div>
            </Transition>
        </div>
    </div>
</template>

<style>
.v-enter-active,
.v-leave-active {
    transition: opacity 0.2s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
.pt-header-small{
    padding-top: 3px;
}
</style>
